/**
 * Predi 主题管理（单一数据源）
 * ---------------------------------------------------------------
 * 历史上项目里存在两套约定，导致 dark/light 切换"不彻底"：
 *   1) documentElement[data-theme = "night" | "day"]
 *      —— 被大量组件内的 `[data-theme='night'] & {}` 选择器依赖；
 *   2) body[data-theme = "dark" | "light"]
 *      —— 被 `mixin.scss` / `home.scss` / `override.scss` 里的
 *         `[data-theme="dark"] & {}` 与 `body[data-theme="dark"] {}` 依赖。
 *
 * 本模块把这两套约定统一在一处维护：任何一次主题变更都同时写
 * html 与 body，并附带 `color-scheme`，避免"半截生效"。
 *
 * 对外统一使用应用层词汇：'night' | 'day'（保持与旧代码/模板兼容）。
 */

export const THEME_KEY = 'currentTheme';

/** 把任意历史值归一化为 'night' | 'day' */
export function normalizeTheme(value) {
  if (value === 'night' || value === 'dark') return 'night';
  if (value === 'day' || value === 'light') return 'day';
  return '';
}

/** 读取用户显式选择的主题；未选择过返回 null（表示"跟随系统"） */
export function getStoredTheme() {
  try {
    const raw = localStorage.getItem(THEME_KEY);
    return raw ? normalizeTheme(raw) || null : null;
  } catch (e) {
    return null;
  }
}

/** 读取系统偏好：'night' | 'day' */
export function getSystemTheme() {
  try {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'night' : 'day';
    }
  } catch (e) {
    /* ignore */
  }
  return 'day';
}

/** 解析最终生效的主题：显式选择优先，否则跟随系统 */
export function resolveTheme() {
  return getStoredTheme() || getSystemTheme();
}

/**
 * 把主题应用到 DOM（不做持久化）。
 * @param {'night'|'day'} theme
 */
export function applyTheme(theme) {
  const t = normalizeTheme(theme) || 'day';
  const isNight = t === 'night';

  if (typeof document === 'undefined') return t;

  const root = document.documentElement;
  root.setAttribute('data-theme', isNight ? 'night' : 'day');
  root.setAttribute('data-color-scheme', isNight ? 'dark' : 'light');
  // 让浏览器原生控件（滚动条/表单/自动填充）跟随主题
  root.style.colorScheme = isNight ? 'dark' : 'light';

  if (document.body) {
    document.body.setAttribute('data-theme', isNight ? 'dark' : 'light');
    document.body.classList.toggle('theme-dark', isNight);
    document.body.classList.toggle('theme-light', !isNight);
  }

  // 移动端浏览器地址栏 / 状态栏颜色
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', isNight ? '#0a0a0f' : '#ffffff');
  }

  return t;
}

/**
 * 应用并持久化主题，同时广播变更事件（供跨组件/跨标签页同步）。
 * @param {'night'|'day'} theme
 */
export function setTheme(theme) {
  const t = applyTheme(theme);
  try {
    localStorage.setItem(THEME_KEY, t);
  } catch (e) {
    /* ignore */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('predi:themechange', { detail: { theme: t } }));
  }
  return t;
}

/** 在用户显式选择与"跟随系统"之间切换 */
export function toggleTheme(current) {
  const cur = normalizeTheme(current) || resolveTheme();
  return setTheme(cur === 'night' ? 'day' : 'night');
}

/** 跟随系统主题变化（仅当用户没有显式选择时生效） */
export function watchSystemTheme(callback) {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = () => {
    if (getStoredTheme()) return; // 用户已显式选择，忽略系统变化
    const t = resolveTheme();
    applyTheme(t);
    callback && callback(t);
  };
  mql.addEventListener ? mql.addEventListener('change', handler) : mql.addListener(handler);
  return () => {
    mql.removeEventListener ? mql.removeEventListener('change', handler) : mql.removeListener(handler);
  };
}

/**
 * 监听其它标签页的主题变更（storage 事件），保持多标签一致。
 */
export function watchStorage(callback) {
  if (typeof window === 'undefined') return () => {};
  const handler = (e) => {
    if (e.key !== THEME_KEY) return;
    const t = normalizeTheme(e.newValue) || resolveTheme();
    applyTheme(t);
    callback && callback(t);
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}

/** 启动时初始化：在 Vue 挂载前尽早调用，避免首屏闪烁 */
export function initTheme() {
  const t = resolveTheme();
  applyTheme(t);
  return t;
}

/**
 * 把后端下发的品牌色（通常是为浅底调暗过的深色）在深色底上提亮。
 * ---------------------------------------------------------------
 * 深色底与浅色底对文字色亮度的要求是**互斥**的：
 *   - 白底要 ≥4.5:1  → 亮度 L ≤ 0.183
 *   - #0a0a0f 底要 ≥4.5:1 → 亮度 L ≥ 0.189
 * 不存在一个色值能同时满足两边，所以夜间必须做一次「向白色混合」的提亮。
 *
 * @param {string} color 形如 #rrggbb / #rgb 的颜色
 * @param {number} amount 向白色混合的比例（0~1，越大越亮）
 * @returns {string} 提亮后的 #rrggbb；无法解析时原样返回
 */
/**
 * 归一化后端下发的分类色。
 *
 * 历史数据里有几个分类的 `color` 落库时漏了 `#`（如 "4f8fef"），
 * 直接拿去当 style 值会是无效颜色（标签就没了颜色）。
 * 这里补 `#`，并顺带把 3 位缩写展开；无法解析时原样返回。
 *
 * @param {string} color
 * @returns {string}
 */
export function normalizeHexColor(color) {
  const raw = String(color || '').trim();
  const body = raw.replace(/^#/, '');
  if (/^[0-9a-fA-F]{6}$/.test(body)) {
    return '#' + body;
  }
  if (/^[0-9a-fA-F]{3}$/.test(body)) {
    return '#' + body[0] + body[0] + body[1] + body[1] + body[2] + body[2];
  }
  return raw;
}

export function lightenForDark(color, amount = 0.55) {
  const m = String(color || '').trim().replace(/^#/, '');
  let hex = m;
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return color;
  const ch = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const mixed = ch.map((v) => Math.round(v + (255 - v) * amount));
  return (
    '#' +
    mixed
      .map((v) => v.toString(16).padStart(2, '0'))
      .join('')
  );
}

export default {
  THEME_KEY,
  normalizeTheme,
  getStoredTheme,
  getSystemTheme,
  resolveTheme,
  applyTheme,
  setTheme,
  toggleTheme,
  watchSystemTheme,
  watchStorage,
  initTheme,
  lightenForDark,
  normalizeHexColor,
};
